import { sidebarConfigs } from "@/config/sidebar-config";
import {
  DashboardApp,
  getAppFromPathname,
} from "@/utils/app-route.utils";

const BRAND = "Loyyal";

const APP_DISPLAY_NAMES: Record<DashboardApp, string> = {
  accesspoint: "Access Point",
  perkpoint: "Perk Point",
  gamepoint: "Game Point",
  xpandpoint: "Xpand Point",
  rewardpoint: "Reward Point",
  giftcard: "Gift OS",
  offers: "Offer Point",
};

const PUBLIC_PAGE_TITLES: Array<{ match: RegExp | string; title: string }> = [
  { match: "/login", title: "Login" },
  { match: "/forgot-password", title: "Forgot Password" },
  { match: "/password-changed-success", title: "Password Changed" },
  { match: "/auth/verify-mfa", title: "Verify MFA" },
  { match: "/auth/verify", title: "Verify Email" },
  { match: "/spin-wheel", title: "Spin Wheel" },
  { match: "/scratch-card", title: "Scratch Card" },
  { match: "/slot-machine", title: "Slot Machine" },
  { match: "/mystery-box", title: "Mystery Box" },
  { match: "/publicoffers", title: "Public Offers" },
  { match: "/offers/storefront", title: "Offer Storefront" },
  { match: "/member-details", title: "Member Details" },
  { match: "/member/login", title: "Member Login" },
  { match: "/member/hotels", title: "Rewards" },
  { match: "/member/bookings", title: "My Bookings" },
  { match: "/member/booking", title: "Booking" },
  { match: "/member/profile", title: "My Profile" },
  { match: "/member/offers", title: "Offers" },
  { match: "/member/transactions", title: "Transactions" },
  { match: "/member", title: "Perk Point" },
  { match: "/quiz", title: "Quiz" },
  { match: "/invoices/print", title: "Print Invoice" },
];

const PAGE_OVERRIDES: Array<{ match: RegExp; title: string }> = [
  { match: /^\/accesspoint\/programs\/create$/, title: "Create Program" },
  { match: /^\/accesspoint\/programs\/[^/]+$/, title: "Program Details" },
  { match: /^\/accesspoint\/merchants\/create$/, title: "Create Merchant" },
  { match: /^\/accesspoint\/merchants\/[^/]+$/, title: "Merchant Details" },
  { match: /^\/accesspoint\/clients\/create$/, title: "Create Client" },
  { match: /^\/accesspoint\/clients\/[^/]+$/, title: "Client Details" },
  { match: /^\/accesspoint\/transactions$/, title: "Transactions" },
  { match: /^\/accesspoint\/accounting-entry$/, title: "Accounting Entries" },
  { match: /^\/accesspoint\/analytics$/, title: "Analytics" },

  { match: /^\/offers\/programs\/create$/, title: "Create Program" },
  { match: /^\/offers\/programs\/[^/]+$/, title: "Program Details" },
  { match: /^\/offers\/merchants\/create$/, title: "Create Merchant" },
  { match: /^\/offers\/merchants\/[^/]+$/, title: "Merchant Details" },

  { match: /^\/perkpoint\/create-program$/, title: "Create Program" },
  { match: /^\/perkpoint\/program\/[^/]+$/, title: "Program Details" },
  { match: /^\/perkpoint\/create-member$/, title: "Create Member" },
  { match: /^\/perkpoint\/member\/[^/]+$/, title: "Member Details" },
  { match: /^\/perkpoint\/members$/, title: "Members" },
  { match: /^\/perkpoint\/programs$/, title: "Programs" },
  { match: /^\/perkpoint\/transactions$/, title: "Transactions" },
  { match: /^\/perkpoint\/ai-assistance$/, title: "AI Assistance" },

  { match: /^\/xpandpoint\/programs\/create$/, title: "Create Program" },
  { match: /^\/xpandpoint\/partner\/create$/, title: "Create Partner" },
  { match: /^\/xpandpoint\/programs\/[^/]+$/, title: "Program Details" },
  { match: /^\/xpandpoint\/partners\/[^/]+$/, title: "Partner Details" },
  { match: /^\/xpandpoint\/transfer-partners\/create$/, title: "Create Transfer Partner" },
  { match: /^\/xpandpoint\/transfer-programs\/create$/, title: "Create Transfer Program" },
  { match: /^\/xpandpoint\/transfer-partners\/[^/]+$/, title: "Transfer Partner Details" },
  { match: /^\/xpandpoint\/transfer-programs\/[^/]+$/, title: "Transfer Program Details" },
  { match: /^\/xpandpoint\/programs$/, title: "Programs" },
  { match: /^\/xpandpoint\/accounting-entry$/, title: "Accounting Entries" },
  { match: /^\/xpandpoint\/analytics$/, title: "Analytics" },
  { match: /^\/xpandpoint\/clients\/create$/, title: "Create Client" },
  { match: /^\/xpandpoint\/clients\/[^/]+$/, title: "Client Details" },

  { match: /^\/rewardpoint\/programs\/create$/, title: "Create Program" },
  { match: /^\/rewardpoint\/programs\/[^/]+$/, title: "Program Details" },
  { match: /^\/rewardpoint\/program-categories$/, title: "Program Categories" },
  { match: /^\/rewardpoint\/products\/merchandise$/, title: "Merchandise" },
  { match: /^\/rewardpoint\/products\/flight-booking$/, title: "Flight Booking" },
  { match: /^\/rewardpoint\/products\/hotel-booking$/, title: "Hotel Booking" },
  { match: /^\/rewardpoint\/products\/gift-cards\/create$/, title: "Create Gift Card Product" },
  { match: /^\/rewardpoint\/products\/merchandise\/create$/, title: "Create Merchandise" },
  { match: /^\/rewardpoint\/products\/flight-booking\/create$/, title: "Create Flight Booking" },
  { match: /^\/rewardpoint\/products\/hotel-booking\/create$/, title: "Create Hotel Booking" },
  { match: /^\/rewardpoint\/products\/gift-card-view\/[^/]+$/, title: "Gift Card Product" },
  { match: /^\/rewardpoint\/products\/merchandise-view\/[^/]+$/, title: "Merchandise Product" },
  { match: /^\/rewardpoint\/products\/flight-booking-view\/[^/]+$/, title: "Flight Booking Product" },
  { match: /^\/rewardpoint\/products\/hotel-booking-view\/[^/]+$/, title: "Hotel Booking Product" },
  { match: /^\/rewardpoint\/orders\/[^/]+$/, title: "Order Details" },
  { match: /^\/rewardpoint\/redemption-merchants\/create$/, title: "Create Redemption Merchant" },
  { match: /^\/rewardpoint\/redemption-merchants\/view\/[^/]+$/, title: "Redemption Merchant" },
  { match: /^\/rewardpoint\/shipping-profiles\/create$/, title: "Create Shipping Profile" },
  { match: /^\/rewardpoint\/shipping-profiles\/[^/]+$/, title: "Shipping Profile Details" },
  { match: /^\/rewardpoint\/clients\/create$/, title: "Create Client" },
  { match: /^\/rewardpoint\/clients\/[^/]+$/, title: "Client Details" },
  { match: /^\/rewardpoint$/, title: "Programs" },

  { match: /^\/giftcard\/issue$/, title: "Issue Gift Card" },
  { match: /^\/giftcard\/create-new$/, title: "Create Gift Card" },
  { match: /^\/giftcard\/brands\/create$/, title: "Create Brand" },

  { match: /^\/users$/, title: "Users" },
  { match: /^\/settings$/, title: "Settings" },
];

function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname || "/";
}

function getSidebarPageTitle(
  pathname: string,
  app: DashboardApp,
): string | null {
  const config = sidebarConfigs[app];
  if (!config) return null;

  const items = config.groups
    .flatMap((group) => group.items)
    .flatMap((item) =>
      item.children?.length ? [item, ...item.children] : [item],
    )
    .filter(
      (item) =>
        !item.external && Boolean(item.href) && item.href!.startsWith("/"),
    );

  const exact = items.find(
    (item) => item.href && normalizePath(item.href) === pathname,
  );
  if (exact) return exact.name;

  const prefixMatch = items
    .filter((item) => {
      if (!item.href) return false;
      const href = normalizePath(item.href);
      return href !== "/" && pathname.startsWith(`${href}/`);
    })
    .sort((a, b) => (b.href?.length || 0) - (a.href?.length || 0))[0];

  return prefixMatch?.name ?? null;
}

function getPublicPageTitle(pathname: string): string | null {
  for (const entry of PUBLIC_PAGE_TITLES) {
    if (typeof entry.match === "string") {
      if (
        pathname === entry.match ||
        pathname.startsWith(`${entry.match}/`)
      ) {
        return entry.title;
      }
    } else if (entry.match.test(pathname)) {
      return entry.title;
    }
  }
  return null;
}

function getOverridePageTitle(pathname: string): string | null {
  for (const entry of PAGE_OVERRIDES) {
    if (entry.match.test(pathname)) {
      return entry.title;
    }
  }
  return null;
}

function humanizeSegment(segment: string): string {
  return segment
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getFallbackPageTitle(pathname: string, app: DashboardApp | null): string {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return "Dashboard";

  const appPrefixes = [
    "accesspoint",
    "perkpoint",
    "gamepoint",
    "xpandpoint",
    "rewardpoint",
    "giftcard",
    "offers",
  ];
  const meaningful = parts.filter((part) => !appPrefixes.includes(part));
  if (meaningful.length === 0) {
    return app ? "Dashboard" : "Home";
  }

  const last = meaningful[meaningful.length - 1];
  if (/^\d+$/.test(last) || /^[0-9a-f-]{8,}$/i.test(last)) {
    const parent = meaningful[meaningful.length - 2];
    return parent ? `${humanizeSegment(parent)} Details` : "Details";
  }

  return humanizeSegment(last);
}

export function buildDocumentTitle(pathname: string): string {
  const { pageTitle, appName } = getRouteWayfinding(pathname);

  if (!appName) {
    return `${pageTitle} · ${BRAND}`;
  }

  return `${pageTitle} · ${appName} · ${BRAND}`;
}

/** Page + app labels for mobile header / wayfinding UI. */
export function getRouteWayfinding(pathname: string): {
  pageTitle: string;
  appName: string | null;
} {
  const path = normalizePath(pathname);

  const publicTitle = getPublicPageTitle(path);
  if (publicTitle) {
    return { pageTitle: publicTitle, appName: null };
  }

  const app = getAppFromPathname(path);
  if (!app) {
    return { pageTitle: "Page Not Found", appName: null };
  }

  const pageTitle =
    getOverridePageTitle(path) ||
    getSidebarPageTitle(path, app) ||
    getFallbackPageTitle(path, app);

  return {
    pageTitle,
    appName: APP_DISPLAY_NAMES[app],
  };
}

export function setDocumentTitle(pathname: string): void {
  document.title = buildDocumentTitle(pathname);
}
